/**
 * Overwatch Error Capture Plugin
 * Automatically captures and reports errors to the Overwatch API
 */
import type { App } from 'vue';

export interface OverwatchConfig {
  apiUrl: string;
  apiKey: string;
  environment?: 'development' | 'staging' | 'production';
  enabled?: boolean;
  captureUnhandledRejections?: boolean;
  captureGlobalErrors?: boolean;
  beforeSend?: (error: ErrorPayload) => ErrorPayload | null;
}

export interface ErrorPayload {
  message: string;
  stack?: string;
  environment: 'development' | 'staging' | 'production';
  metadata?: {
    url?: string;
    userAgent?: string;
    userId?: string;
    custom?: Record<string, unknown>;
  };
}

export interface OverwatchResponse {
  success: boolean;
  data?: {
    id: string;
    fingerprint: string;
    occurrenceCount: number;
    isNew: boolean;
    firstSeen: string;
    lastSeen: string;
  };
  error?: string;
}

class OverwatchClient {
  private config: Required<Omit<OverwatchConfig, 'beforeSend'>> & Pick<OverwatchConfig, 'beforeSend'>;
  private userId: string | null = null;

  constructor(config: OverwatchConfig) {
    this.config = {
      apiUrl: config.apiUrl,
      apiKey: config.apiKey,
      environment: config.environment || 'production',
      enabled: config.enabled ?? true,
      captureUnhandledRejections: config.captureUnhandledRejections ?? true,
      captureGlobalErrors: config.captureGlobalErrors ?? true,
      beforeSend: config.beforeSend,
    };
  }

  /**
   * Set the current user ID for error attribution
   */
  setUser(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Manually capture and report an error
   */
  async captureError(
    error: Error | string,
    customMetadata?: Record<string, unknown>
  ): Promise<OverwatchResponse | null> {
    if (!this.config.enabled) {
      return null;
    }

    const err = error instanceof Error ? error : new Error(String(error));

    let payload: ErrorPayload = {
      message: err.message,
      stack: err.stack,
      environment: this.config.environment,
      metadata: {
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        userId: this.userId || undefined,
        custom: customMetadata,
      },
    };

    // Allow modification or filtering via beforeSend
    if (this.config.beforeSend) {
      const modified = this.config.beforeSend(payload);
      if (modified === null) {
        console.debug('[Overwatch] Error filtered by beforeSend');
        return null;
      }
      payload = modified;
    }

    return this.sendError(payload);
  }

  /**
   * Capture a message as an error (for logging non-Error events)
   */
  async captureMessage(
    message: string,
    customMetadata?: Record<string, unknown>
  ): Promise<OverwatchResponse | null> {
    return this.captureError(new Error(message), customMetadata);
  }

  /**
   * Send error payload to the Overwatch API
   */
  private async sendError(payload: ErrorPayload): Promise<OverwatchResponse> {
    try {
      const response = await fetch(`${this.config.apiUrl}/errors/ingest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('[Overwatch] Failed to report error:', result.error || response.statusText);
        return { success: false, error: result.error || response.statusText };
      }

      console.debug('[Overwatch] Error reported:', result.data?.fingerprint);
      return result;
    } catch (err) {
      console.error('[Overwatch] Failed to send error:', err);
      return { success: false, error: String(err) };
    }
  }

  /**
   * Setup global error handlers
   */
  setupGlobalHandlers(): void {
    if (typeof window === 'undefined') return;

    // Capture unhandled errors
    if (this.config.captureGlobalErrors) {
      window.addEventListener('error', (event) => {
        this.captureError(event.error || event.message, {
          type: 'window.onerror',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      });
    }

    // Capture unhandled promise rejections
    if (this.config.captureUnhandledRejections) {
      window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

        this.captureError(error, {
          type: 'unhandledrejection',
        });
      });
    }
  }

  /**
   * Get Vue error handler function
   */
  getVueErrorHandler() {
    return (err: unknown, instance: unknown, info: string) => {
      const error = err instanceof Error ? err : new Error(String(err));

      this.captureError(error, {
        type: 'vue',
        componentInfo: info,
        componentName: (instance as { $options?: { name?: string } })?.$options?.name,
      });

      // Still log to console for development
      console.error('[Vue Error]', error);
      console.error('Component info:', info);
    };
  }
}

// Global instance
let overwatchInstance: OverwatchClient | null = null;

/**
 * Get the global Overwatch instance
 */
export function useOverwatch(): OverwatchClient {
  if (!overwatchInstance) {
    throw new Error('Overwatch not initialized. Call app.use(overwatch, config) first.');
  }
  return overwatchInstance;
}

/**
 * Vue plugin installation
 */
export const overwatch = {
  install(app: App, config: OverwatchConfig): void {
    overwatchInstance = new OverwatchClient(config);

    // Setup Vue's global error handler
    app.config.errorHandler = overwatchInstance.getVueErrorHandler();

    // Setup global window handlers
    overwatchInstance.setupGlobalHandlers();

    // Provide instance globally
    app.provide('overwatch', overwatchInstance);
    app.config.globalProperties.$overwatch = overwatchInstance;

    console.log('[Overwatch] Initialized', {
      apiUrl: config.apiUrl,
      environment: config.environment,
      enabled: config.enabled ?? true,
    });
  },
};

export { OverwatchClient };
export default overwatch;
